import { VideoPlatform } from '@ztechapps/common.contract'

/**
 * Represents an extracted video URL with its detected platform.
 */
export type ExtractedVideo = {
  /** The extracted video URL */
  url: string
  /** The detected video platform */
  platform: VideoPlatform
}

/**
 * Regex patterns for detecting various video URL formats in HTML content.
 *
 * YouTube patterns:
 * - youtube.com/watch?v=VIDEO_ID
 * - youtu.be/VIDEO_ID
 * - youtube.com/embed/VIDEO_ID
 * - youtube-nocookie.com/embed/VIDEO_ID
 *
 * Google Drive patterns:
 * - drive.google.com/file/d/FILE_ID/view
 * - drive.google.com/open?id=FILE_ID
 *
 * DigitalOcean CDN patterns:
 * - *.nyc3.cdn.digitaloceanspaces.com/*.mp4
 *
 * Direct MP4 patterns:
 * - Any URL ending with .mp4
 */
const VIDEO_PATTERNS = {
  // YouTube patterns - capturing full URLs
  youtube: [
    // youtube.com/watch?v=VIDEO_ID (with optional additional params)
    /https?:\/\/(?:www\.)?youtube\.com\/watch\?[^\s"'<>]*v=[a-zA-Z0-9_-]+[^\s"'<>]*/gi,
    // youtu.be/VIDEO_ID
    /https?:\/\/youtu\.be\/[a-zA-Z0-9_-]+[^\s"'<>]*/gi,
    // youtube.com/embed/VIDEO_ID
    /https?:\/\/(?:www\.)?youtube\.com\/embed\/[a-zA-Z0-9_-]+[^\s"'<>]*/gi,
    // youtube-nocookie.com/embed/VIDEO_ID
    /https?:\/\/(?:www\.)?youtube-nocookie\.com\/embed\/[a-zA-Z0-9_-]+[^\s"'<>]*/gi,
  ],

  // Google Drive video patterns
  googleDrive: [
    // drive.google.com/file/d/FILE_ID/view or /preview
    /https?:\/\/drive\.google\.com\/file\/d\/[a-zA-Z0-9_-]+(?:\/[^\s"'<>]*)?/gi,
    // drive.google.com/open?id=FILE_ID
    /https?:\/\/drive\.google\.com\/open\?id=[a-zA-Z0-9_-]+[^\s"'<>]*/gi,
  ],

  // DigitalOcean CDN patterns - specifically for .mp4 files on nyc3 spaces
  digitaloceanCdn: [
    /https?:\/\/[a-zA-Z0-9-]+\.nyc3\.cdn\.digitaloceanspaces\.com\/[^\s"'<>]*\.mp4[^\s"'<>]*/gi,
    // Also support non-CDN spaces URLs
    /https?:\/\/[a-zA-Z0-9-]+\.nyc3\.digitaloceanspaces\.com\/[^\s"'<>]*\.mp4[^\s"'<>]*/gi,
  ],

  // Direct MP4 links - generic .mp4 URLs (excluding already matched patterns)
  directMp4: [/https?:\/\/[^\s"'<>]+\.mp4(?:\?[^\s"'<>]*)?/gi],
}

/**
 * Extracts video URLs from HTML content and categorizes them by platform.
 *
 * This utility scans HTML content for video URLs from various platforms:
 * - YouTube (watch, short, embed URLs)
 * - Google Drive video files
 * - DigitalOcean CDN MP4 files
 * - Direct MP4 links
 *
 * @param contentHtml - The HTML content to scan for video URLs
 * @returns Array of ExtractedVideo objects with url and platform fields
 *
 * @example
 * ```typescript
 * const html = '<iframe src="https://youtube.com/embed/abc123"></iframe>'
 * const videos = extractVideoUrls(html)
 * // Returns: [{ url: 'https://youtube.com/embed/abc123', platform: 'youtube' }]
 * ```
 */
export function extractVideoUrls(contentHtml: string): ExtractedVideo[] {
  if (!contentHtml) {
    return []
  }

  const extractedUrls = new Map<string, ExtractedVideo>()

  // Extract YouTube URLs
  for (const pattern of VIDEO_PATTERNS.youtube) {
    const matches = contentHtml.match(pattern) ?? []
    for (const url of matches) {
      const cleanUrl = cleanExtractedUrl(url)
      if (!extractedUrls.has(cleanUrl)) {
        extractedUrls.set(cleanUrl, { url: cleanUrl, platform: VideoPlatform.youtube })
      }
    }
  }

  // Extract Google Drive URLs
  for (const pattern of VIDEO_PATTERNS.googleDrive) {
    const matches = contentHtml.match(pattern) ?? []
    for (const url of matches) {
      const cleanUrl = cleanExtractedUrl(url)
      if (!extractedUrls.has(cleanUrl)) {
        extractedUrls.set(cleanUrl, { url: cleanUrl, platform: VideoPlatform.google_drive })
      }
    }
  }

  // Extract DigitalOcean CDN URLs
  for (const pattern of VIDEO_PATTERNS.digitaloceanCdn) {
    const matches = contentHtml.match(pattern) ?? []
    for (const url of matches) {
      const cleanUrl = cleanExtractedUrl(url)
      if (!extractedUrls.has(cleanUrl)) {
        extractedUrls.set(cleanUrl, { url: cleanUrl, platform: VideoPlatform.digitalocean_cdn })
      }
    }
  }

  // Extract direct MP4 URLs (excluding already matched URLs)
  for (const pattern of VIDEO_PATTERNS.directMp4) {
    const matches = contentHtml.match(pattern) ?? []
    for (const url of matches) {
      const cleanUrl = cleanExtractedUrl(url)
      // Skip if already extracted (e.g., DigitalOcean CDN URLs ending in .mp4)
      if (!extractedUrls.has(cleanUrl)) {
        // Determine if this is a known platform or generic direct MP4
        const platform = isDigitalOceanUrl(cleanUrl) ? VideoPlatform.digitalocean_cdn : VideoPlatform.direct_mp4
        extractedUrls.set(cleanUrl, { url: cleanUrl, platform })
      }
    }
  }

  return Array.from(extractedUrls.values())
}

/**
 * Cleans an extracted URL by removing trailing punctuation and HTML artifacts.
 *
 * @param url - The raw extracted URL
 * @returns Cleaned URL string
 */
function cleanExtractedUrl(url: string): string {
  // Remove trailing quotes, angle brackets, and common HTML artifacts
  return url.replace(/["'<>]+$/, '').replace(/&amp;/g, '&')
}

/**
 * Checks if a URL is from DigitalOcean Spaces.
 *
 * @param url - The URL to check
 * @returns True if the URL is from DigitalOcean Spaces
 */
function isDigitalOceanUrl(url: string): boolean {
  return url.includes('.digitaloceanspaces.com')
}
